import type { CampingSpot } from '../types';

export type RoadAccessClass = NonNullable<CampingSpot['roadAccessClass']>;
export type RoadAccessSource = NonNullable<CampingSpot['roadAccessSource']>;

const PATH_HIGHWAYS = new Set(['path', 'footway', 'cycleway', 'bridleway', 'pedestrian', 'steps']);
const FOREST_ROAD_CATEGORIES = new Set(['S']);
const PUBLIC_ROAD_CATEGORIES = new Set(['E', 'R', 'F', 'K']);
// Kjente OSM highway-verdier som betyr ekte bilvei. Allow-list er tryggere enn sentinel-deny-list,
// fordi visningsstrenger ('Natur', 'Vei') og uoversatte i18n-keys ('spot.nature') ikke skal feilklassifiseres.
// `unclassified` ekskluderes med vilje — i bygde-Norge brukes det for grusveier som ofte er
// skogsveier; faller gjennom til skogsvei-default for å være filtrerbar via Skogsbilveger-toggle.
const PUBLIC_OSM_HIGHWAYS = new Set([
  'motorway', 'motorway_link', 'trunk', 'trunk_link',
  'primary', 'primary_link', 'secondary', 'secondary_link',
  'tertiary', 'tertiary_link', 'residential', 'living_street'
]);

export function deriveRoadAccessClass(spot: Partial<CampingSpot>): {
  roadAccessClass: RoadAccessClass;
  roadAccessSource: RoadAccessSource;
} {
  if (spot.roadAccessClass && spot.roadAccessClass !== 'unknown') {
    return {
      roadAccessClass: spot.roadAccessClass,
      roadAccessSource: spot.roadAccessSource || 'derived',
    };
  }

  const accessRoadType = String(spot.accessRoadType || '').toLowerCase();
  if (accessRoadType === 'skogsbilveg') return { roadAccessClass: 'forest_road', roadAccessSource: 'kartverket_wfs' };
  if (accessRoadType === 'traktorveg') return { roadAccessClass: 'tractor_road', roadAccessSource: 'kartverket_wfs' };
  if (accessRoadType === 'sti') return { roadAccessClass: 'path', roadAccessSource: 'kartverket_wfs' };
  if (accessRoadType === 'public') return { roadAccessClass: 'public', roadAccessSource: 'kartverket_wfs' };
  if (accessRoadType === 'serviceveg' || accessRoadType === 'servicevei') {
    return { roadAccessClass: 'service_road', roadAccessSource: 'kartverket_wfs' };
  }

  const roadCategory = String((spot as any).roadCategory || spot.publicRoadConnection?.category || '').toUpperCase();
  if (FOREST_ROAD_CATEGORIES.has(roadCategory)) return { roadAccessClass: 'forest_road', roadAccessSource: 'nvdb' };
  if (roadCategory === 'P') return { roadAccessClass: 'service_road', roadAccessSource: 'nvdb' };
  if (PUBLIC_ROAD_CATEGORIES.has(roadCategory)) return { roadAccessClass: 'public', roadAccessSource: 'nvdb' };

  // Navn/notat-hint sjekkes FØR generisk OSM `highway`-mapping. I Norge tagges
  // skogsbilveger ofte som `highway=track` (OSM-skjønn), men brukeren forventer
  // at "Skogsbilveger"-laget kontrollerer dem. Samme for `unclassified` som
  // dekker en blanding av offentlige småveier og skogsveier — naveheuristikken
  // er den eneste pålitelige diskriminatoren.
  const textHint = `${spot.name || ''} ${spot.description || ''}`.toLowerCase();
  const isForestRoadByName =
    textHint.includes('skogsvei') ||
    textHint.includes('skogsbilveg') ||
    textHint.includes('skogsbilvei') ||
    textHint.includes('forest road');
  if (isForestRoadByName) {
    return { roadAccessClass: 'forest_road', roadAccessSource: 'derived' };
  }
  const isTractorRoadByName =
    textHint.includes('traktorveg') ||
    textHint.includes('traktorvei') ||
    textHint.includes('tractor road');
  if (isTractorRoadByName) {
    return { roadAccessClass: 'tractor_road', roadAccessSource: 'derived' };
  }
  const isServiceRoadByName =
    textHint.includes('servicevei') ||
    textHint.includes('serviceveg') ||
    textHint.includes('service road') ||
    textHint.includes('driftsvei') ||
    textHint.includes('adkomstvei');
  if (isServiceRoadByName) {
    return { roadAccessClass: 'service_road', roadAccessSource: 'derived' };
  }

  const highway = String(spot.highway || '').toLowerCase();
  if (PATH_HIGHWAYS.has(highway)) return { roadAccessClass: 'path', roadAccessSource: 'osm' };
  if (PUBLIC_OSM_HIGHWAYS.has(highway)) return { roadAccessClass: 'public', roadAccessSource: 'osm' };
  if (highway === 'service') return { roadAccessClass: 'service_road', roadAccessSource: 'osm' };
  // Standard for usikre tilfeller: skogsbilveg. Fanger `highway=track`,
  // `highway=unclassified` (ofte grusveier i skogen) og spots uten highway-tag.
  // Eksplisitt traktor/service-navn fanges av navn-hint over og overstyrer.
  // Resultat: alt som ikke er en bekreftet bilvei eller sti rendres under
  // Skogsbilveger-toggelen, så brukeren har én pålitelig kontroll for off-road.
  return { roadAccessClass: 'forest_road', roadAccessSource: 'derived' };
}

export function hasOffroadRoadAccess(spot: Partial<CampingSpot>): boolean {
  const { roadAccessClass } = deriveRoadAccessClass(spot);
  return roadAccessClass === 'forest_road' || roadAccessClass === 'tractor_road' || roadAccessClass === 'service_road';
}
